// src/pages/financials/BatchApprovalQueue.js
// Updated: 01/13/2026 12:10 AM ET

import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Table, TableBody, TableCell, 
  TableHead, TableRow, Button, Chip, Box, CircularProgress, Alert 
} from '@mui/material';
import { CheckCircle, Visibility, History, Refresh } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BatchApprovalQueue = () => {
  const { userProfile, currentUser, isInitialized } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const churchId = userProfile?.churchId;

  useEffect(() => {
    // GUARD: Only start listener if Auth is fully ready and user has a churchId
    if (!isInitialized || !churchId) {
        if (isInitialized && !churchId) setLoading(false);
        return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'churches', churchId, 'financials', 'data', 'batches'),
      where('status', '==', 'draft'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const batchData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setBatches(batchData);
      setLoading(false);
    }, (err) => {
      console.error("Queue Sync Error:", err);
      setLoading(false);
    });

    // CLEANUP: Essential to prevent Firestore "Internal Assertion" errors
    return () => {
        if (unsubscribe) unsubscribe();
    };
  }, [churchId, isInitialized]);

  const handleReview = (batchId) => {
    navigate(`/financials/batch-entry?batchId=${batchId}`);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Syncing with Vault...</Typography>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Verification Queue</Typography>
        <Box>
            <Button startIcon={<Refresh />} onClick={() => window.location.reload()} sx={{ mr: 1 }}>Sync Session</Button>
            <Button startIcon={<History />} variant="outlined" onClick={() => navigate('/financials')}>Dashboard</Button>
        </Box>
      </Box>

      {!churchId && (
        <Alert severity="warning" sx={{ mb: 2 }}>Church profile not loaded. Please refresh.</Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Internal Control:</strong> You are in "Checker" mode. Select a batch created by another staff member to verify and post to the ledger.
      </Alert>

      <Paper elevation={3}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
            <TableRow>
              <TableCell>Creation Date</TableCell>
              <TableCell>Maker (Created By)</TableCell>
              <TableCell align="center">Entries</TableCell>
              <TableCell align="right">Target Total</TableCell>
              <TableCell align="right">Entry Total</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">All batches have been cleared and verified.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              batches.map((batch) => {
                const isMaker = batch.createdBy === currentUser?.uid;
                return (
                  <TableRow key={batch.id} hover>
                    <TableCell>
                      {batch.createdAt?.toDate ? batch.createdAt.toDate().toLocaleDateString() : 'Saving...'}
                    </TableCell>
                    <TableCell>
                      {isMaker ? <Chip label="You" size="small" color="warning" variant="outlined" /> : "Finance Associate"}
                    </TableCell>
                    <TableCell align="center">{batch.transactionCount || 0}</TableCell>
                    <TableCell align="right">${(batch.controlTotal || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">${(batch.currentTotal || 0).toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Button 
                        variant="contained" 
                        size="small"
                        color={isMaker ? "inherit" : "primary"}
                        startIcon={isMaker ? <Visibility /> : <CheckCircle />}
                        onClick={() => handleReview(batch.id)}
                      >
                        {isMaker ? "Review" : "Verify & Post"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default BatchApprovalQueue;